import type { QueryFunctionContext } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isInDisplayWindow } from 'components/common/ToastNotifications/utils/time';
import groq from 'groq';
import { ToastNotifications, useLocalStorage } from 'hooks/userPreferences';
import { HOME, SKINCARE_HOME } from 'lib/constants/routes';
import { ASSET, DATE_RANGE } from 'lib/queries';
import { client } from 'lib/sanity';
import { usePathname } from 'next/navigation';
import type { ToastNotification } from 'types/cms/ToastNotification';

import { useIsSkincare } from './useIsSkincare';

export const TOAST_KEY = 'toastNotifications';

interface ToastNotificationQueryResult {
  _updatedAt: string;
  notifications: ToastNotification[];
}

export interface ToastNotificationState {
  documentUpdatedAt: string;
  isOpen: boolean;
}

const ALLOWED_PAGES = {
  event: null /* all pages */,
  notification: [HOME, SKINCARE_HOME],
};

const isOnAllowedPage = (
  type: 'event' | 'notification',
  pathname: string,
  pages: Record<string, string[] | null>,
) => (pages[type] ? pages[type].includes(pathname) : true);

const getNotificationState = (
  notification: ToastNotification,
  pathname: string | null,
  storedStates: Record<string, ToastNotificationState>,
) => {
  const type = notification.isEventMode ? 'event' : 'notification';
  const isAllowed = pathname
    ? isOnAllowedPage(type, pathname, ALLOWED_PAGES)
    : true;

  if (!isAllowed) return false;

  const storedState = storedStates[notification._key];
  const shouldUseStoredState =
    storedState?.documentUpdatedAt === notification.documentUpdatedAt;

  if (shouldUseStoredState) {
    return storedState.isOpen;
  }

  if (notification.displayTimes) {
    return isInDisplayWindow(notification.displayTimes);
  }

  return true;
};

const applyStoredState =
  (
    pathname: string | null,
    storedStates: Record<string, ToastNotificationState>,
  ) =>
  (notification: ToastNotification) => {
    const isOpen = getNotificationState(notification, pathname, storedStates);
    return { ...notification, isOpen };
  };

type ToastNotificationKey = [string, string];

export const fetchToastNotifications = async ({
  queryKey,
}: QueryFunctionContext<ToastNotificationKey>) => {
  const [, id] = queryKey;

  const query = groq`
    *[_type == "timeline_popupBanner" && _id == $id][0]{
      _updatedAt,
      "notifications": banners[] {
        _key,
        title,
        subtitle,
        url,
        image { ${ASSET} },
        isEventMode,
        "overline": select(
          isEventMode => null,
          announcement.overline
        ),
        "displayTimes": select(
          isEventMode => {
            "dateRange": event.dateRange { ${DATE_RANGE} },
            "displayBefore": event.displayBefore,
            "displayAfter": event.displayAfter
          },
          null
        )
      }
    }
  `;

  const { _updatedAt: documentUpdatedAt, notifications } =
    await client.fetch<ToastNotificationQueryResult>(query, { id });

  return notifications.map((notification) => ({
    ...notification,
    documentUpdatedAt,
    isOpen: true,
  }));
};

export const useToastNotifiations = () => {
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const isSkincare = useIsSkincare();

  const { value: storedStates, setStoredValue: setStoredStates } =
    useLocalStorage(ToastNotifications);

  const id = isSkincare ? 'skincare_popupBannerDoc' : 'popupBannerDoc';

  const {
    data: notifications,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [TOAST_KEY, id],
    queryFn: fetchToastNotifications,
    select: (data) => data?.map(applyStoredState(pathname, storedStates)),
  });

  const toggleNotification = (key: string, openValue?: boolean) => {
    queryClient.setQueryData<ToastNotification[]>([TOAST_KEY, id], (data) => {
      if (!data) return;

      return data.map((item) => {
        if (item._key !== key) return item;

        const isOpen = openValue !== undefined ? openValue : !item.isOpen;

        setStoredStates((prevState) => ({
          ...prevState,
          [item._key]: {
            documentUpdatedAt: item.documentUpdatedAt,
            isOpen,
          },
        }));

        return { ...item, isOpen };
      });
    });
  };

  return { data: notifications, refetch, isLoading, error, toggleNotification };
};
